import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  Check,
  Clock3,
  ThumbsDown,
  ThumbsUp,
  TrendingUp,
  TriangleAlert,
  X,
} from 'lucide-react'
import { cn } from '../UI/cn'
import { Tooltip } from '../Tooltip/tooltip'
import type {
  EnrichedAction,
  SentimentType,
  ActionClickStatus,
  ActionClickUpdateStatus,
} from '../../services/recommendations_contracts'
import {
  RISK_STYLES,
  URGENCY_ICONS,
  URGENCY_COLORS,
  RISK_TOOLTIPS,
  URGENCY_TOOLTIPS,
} from './constants'

interface EnrichedActionCardProps {
  action: EnrichedAction
  /** Optional callback when the card is clicked (for tracking) */
  onCardClick?: () => void
  /** Optional callback when user provides sentiment feedback (thumbs up/down) */
  onSentimentClick?: (sentiment: SentimentType | null) => void
  /** Initial sentiment value (from persisted data) */
  initialSentiment?: SentimentType | null
  /** Optional callback when user clicks Done/Dismiss */
  onStatusClick?: (status: ActionClickUpdateStatus) => void
  /** Current action status (for visual treatment) */
  actionStatus?: ActionClickStatus | null
}

/**
 * Signal badge component showing "why now" indicators.
 */
interface SignalBadgeProps {
  icon: React.ReactNode
  label: string
  variant: 'danger' | 'warning' | 'info'
}

const badgeVariants = {
  danger: 'bg-error-bg text-error border-error-border',
  warning: 'bg-warning-bg text-warning border-warning-border',
  info: 'bg-surface-sunken text-fg-secondary border-line-subtle',
}

const SignalBadge: React.FC<SignalBadgeProps> = ({ icon, label, variant }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
      badgeVariants[variant]
    )}
  >
    {icon}
    {label}
  </span>
)

const statusBadgeVariants = {
  done: 'bg-fb-green-50 text-fb-dark-green border-fb-green-100',
  dismissed: 'bg-surface-alt text-fg-secondary border-line-subtle',
}

/**
 * Reusable button for Done/Dismiss status actions.
 * Extracts the common pattern shared by both status buttons to avoid DRY violations.
 */
interface ActionStatusButtonProps {
  icon: React.ReactNode
  label: string
  disabled: boolean
  onClick: (e: React.MouseEvent) => void
}

const ActionStatusButton: React.FC<ActionStatusButtonProps> = ({
  icon,
  label,
  disabled,
  onClick,
}) => (
  <button
    onClick={(e) => {
      e.stopPropagation()
      onClick(e)
    }}
    disabled={disabled}
    className={cn(
      'inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors',
      'text-fg-secondary hover:text-fg hover:bg-surface-alt',
      disabled && 'opacity-50 cursor-not-allowed'
    )}
    aria-label={label}
  >
    {icon}
    <span>{label}</span>
  </button>
)

/**
 * Reusable button for sentiment feedback (thumbs up/down).
 * Extracts the common pattern shared by both sentiment buttons to avoid DRY violations.
 */
interface SentimentButtonProps {
  sentimentType: SentimentType
  isActive: boolean
  activeIcon: React.ReactNode
  inactiveIcon: React.ReactNode
  activeClassName: string
  inactiveClassName: string
  ariaLabel: string
  tooltipTitle: string
  tooltipContent: string
  onClick: (e: React.MouseEvent, sentiment: SentimentType) => void
}

const SentimentButton: React.FC<SentimentButtonProps> = ({
  sentimentType,
  isActive,
  activeIcon,
  inactiveIcon,
  activeClassName,
  inactiveClassName,
  ariaLabel,
  tooltipTitle,
  tooltipContent,
  onClick,
}) => (
  <Tooltip
    title={tooltipTitle}
    content={tooltipContent}
    placement="top"
    delay={[300, 0]}
  >
    <button
      onClick={(e) => onClick(e, sentimentType)}
      className={cn(
        'p-1 rounded transition-colors',
        isActive ? activeClassName : inactiveClassName
      )}
      aria-label={ariaLabel}
    >
      {isActive ? activeIcon : inactiveIcon}
    </button>
  </Tooltip>
)

/**
 * Get risk level badge configuration.
 * Always shows the risk level to make priority clear to users.
 *
 * Risk levels indicate the AI's assessment of deal health:
 * - at_risk: Deal showing warning signs, needs immediate attention
 * - opportunity: Deal has positive momentum, ready to advance
 * - routine: Standard follow-up to keep deal progressing
 */
const getRiskBadge = (riskLevel: string): SignalBadgeProps => {
  switch (riskLevel) {
    case 'at_risk':
      return {
        icon: <TriangleAlert size={12} />,
        label: 'Needs Attention',
        variant: 'danger',
      }
    case 'opportunity':
      return {
        icon: <TrendingUp size={12} />,
        label: 'Ready to Advance',
        variant: 'warning',
      }
    default:
      return {
        icon: <Clock3 size={12} />,
        label: 'Follow Up',
        variant: 'info',
      }
  }
}

/**
 * Compact action card with visual risk indicators and company link.
 *
 * Features:
 * - Color-coded risk borders (red=at_risk, yellow=opportunity, gray=routine)
 * - Signal badges showing "why now" reasoning
 * - Link to company page
 * - Context snippets from last interaction
 */
export const EnrichedActionCard: React.FC<EnrichedActionCardProps> = ({
  action,
  onCardClick,
  onSentimentClick,
  initialSentiment = null,
  onStatusClick,
  actionStatus = null,
}) => {
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const [sentiment, setSentiment] = useState<SentimentType | null>(
    initialSentiment
  )
  const textRef = useRef<HTMLParagraphElement>(null)
  const {
    border,
    bg,
    iconColor,
    Icon: RiskIcon,
  } = RISK_STYLES[action.risk_level]
  const UrgencyIcon = URGENCY_ICONS[action.urgency]
  const riskBadge = getRiskBadge(action.risk_level)
  const isStatusFinalized =
    actionStatus === 'done' || actionStatus === 'dismissed'
  const statusLabel =
    actionStatus === 'done'
      ? 'Done'
      : actionStatus === 'dismissed'
        ? 'Dismissed'
        : null
  const statusVariantClass =
    actionStatus === 'done' || actionStatus === 'dismissed'
      ? statusBadgeVariants[actionStatus]
      : null

  const handleCardClick = () => {
    // Call the tracking callback if provided
    if (onCardClick) {
      onCardClick()
    }
    // Navigate to the company page
    navigate(action.company_link)
  }

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Don't navigate when expanding
    setIsExpanded((prev) => !prev)
  }

  const handleSentimentClick = (
    e: React.MouseEvent,
    newSentiment: SentimentType
  ) => {
    e.stopPropagation() // Don't navigate when providing feedback
    // Toggle off if clicking same sentiment, otherwise set new sentiment
    const finalSentiment = sentiment === newSentiment ? null : newSentiment
    setSentiment(finalSentiment)
    if (onSentimentClick) {
      onSentimentClick(finalSentiment)
    }
  }

  // Sync sentiment state with initialSentiment when it changes
  useEffect(() => {
    setSentiment(initialSentiment)
  }, [initialSentiment])

  // Detect if text is actually truncated by line-clamp
  useEffect(() => {
    const el = textRef.current
    if (el && !isExpanded) {
      setIsTruncated(el.scrollHeight > el.clientHeight)
    }
  }, [action.recommendation, isExpanded])

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        'rounded-lg transition-all hover:shadow-md cursor-pointer relative',
        border,
        bg,
        'p-3',
        actionStatus === 'done' && 'opacity-50',
        actionStatus === 'dismissed' && 'opacity-40'
      )}
    >
      <div className="flex items-start gap-2">
        {/* Risk Icon with Tooltip */}
        <Tooltip
          title={RISK_TOOLTIPS[action.risk_level].title}
          content={RISK_TOOLTIPS[action.risk_level].content}
          placement="top"
          delay={[500, 0]}
        >
          <div className={cn('flex-shrink-0 mt-0.5', iconColor)}>
            <RiskIcon size={20} />
          </div>
        </Tooltip>

        <div className="flex-1 min-w-0">
          {/* Header: Action + Urgency + Company Link */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-fg text-sm leading-tight">
                {action.action}
              </h4>
              <div className="flex items-center gap-1.5 mt-0.5 text-xs text-fg-secondary">
                <span className="text-fb-dark-green font-medium">
                  {action.company_name}
                </span>
                {action.company_subcategory && (
                  <>
                    <span className="text-line">·</span>
                    <span className="text-fg-muted">
                      {action.company_subcategory}
                    </span>
                  </>
                )}
                {action.contact_name && (
                  <>
                    <span className="text-line">|</span>
                    <span>{action.contact_name}</span>
                  </>
                )}
              </div>
            </div>
            <div className="ml-2 flex flex-col items-end gap-2 flex-shrink-0">
              {!isStatusFinalized && (
                <Tooltip
                  title="Dismiss"
                  content="Hide this recommendation for now. It may resurface if the situation changes."
                  placement="top"
                  delay={[300, 0]}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onStatusClick?.('dismissed')
                    }}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full text-fg-muted transition-colors hover:bg-surface-alt hover:text-fg-secondary"
                    aria-label="Dismiss"
                  >
                    <X size={14} />
                  </button>
                </Tooltip>
              )}
              <div className="flex items-center gap-2">
                {statusLabel && (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full border px-2 py-0.5 text-xxs font-medium',
                      statusVariantClass
                    )}
                  >
                    {statusLabel}
                  </span>
                )}
                {/* Risk Badge - always shown for clarity */}
                <SignalBadge {...riskBadge} />
                <Tooltip
                  title={URGENCY_TOOLTIPS[action.urgency].title}
                  content={URGENCY_TOOLTIPS[action.urgency].content}
                  placement="top"
                  delay={[500, 0]}
                >
                  <UrgencyIcon
                    size={14}
                    className={URGENCY_COLORS[action.urgency]}
                  />
                </Tooltip>
                <ArrowRight size={16} className="text-fg-muted" />
              </div>
            </div>
          </div>

          {/* Recommendation - expandable */}
          <p
            ref={textRef}
            onClick={handleExpandClick}
            className={cn(
              'text-sm text-fg-secondary mt-1 cursor-pointer',
              !isExpanded && 'line-clamp-4'
            )}
          >
            {action.recommendation}
          </p>
          {!isExpanded && isTruncated && (
            <button
              onClick={handleExpandClick}
              className="text-xs text-fb-dark-green hover:text-fb-dark-green-hover font-medium mt-0.5"
            >
              Show more
            </button>
          )}

          {/* Context Snippet - only if exists and compact */}
          {action.context_snippet && (
            <p className="text-xs text-fg-muted italic mt-1 line-clamp-1">
              &ldquo;{action.context_snippet}&rdquo;
            </p>
          )}

          {/* Action footer: Done button + Sentiment feedback */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-line-subtle">
            <Tooltip
              title="Mark as done"
              content="Completed — hides this for 7 days"
              placement="top"
              delay={[300, 0]}
            >
              <ActionStatusButton
                icon={<Check size={14} />}
                label="Done"
                disabled={isStatusFinalized}
                onClick={() => onStatusClick?.('done')}
              />
            </Tooltip>
            <div className="flex items-center gap-1">
              <span className="text-xs text-fg-muted mr-1">Helpful?</span>
              <SentimentButton
                sentimentType="positive"
                isActive={sentiment === 'positive'}
                activeIcon={<ThumbsUp size={14} />}
                inactiveIcon={<ThumbsUp size={14} />}
                activeClassName="text-fb-green-600 bg-fb-green-50"
                inactiveClassName="text-fg-muted hover:text-fb-green-600 hover:bg-fb-green-50"
                ariaLabel="Mark as helpful"
                tooltipTitle="Helpful"
                tooltipContent="More like this — helps us prioritize similar recommendations"
                onClick={handleSentimentClick}
              />
              <SentimentButton
                sentimentType="negative"
                isActive={sentiment === 'negative'}
                activeIcon={<ThumbsDown size={14} />}
                inactiveIcon={<ThumbsDown size={14} />}
                activeClassName="text-error bg-error-bg"
                inactiveClassName="text-fg-muted hover:text-error hover:bg-error-bg"
                ariaLabel="Mark as not helpful"
                tooltipTitle="Not helpful"
                tooltipContent="Dismiss and hide similar recommendations for 2 weeks"
                onClick={handleSentimentClick}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
